import React, {useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Box,
  BreadcrumbGroup,
  ColumnLayout,
  Container,
  ContentLayout,
  Header,
  Spinner,
} from "@amzn/awsui-components-react";
import {Board, BoardItem, BoardProps} from "@amzn/awsui-board-components";
import DarkModeContext from "../../contexes/DarkModeContext";
import {StageContext} from "../../contexes/OrderIdContext";
import {UATProject} from "../../types/UATProject";
import {fetchUATProject} from "../../helpers/RavenUtils";
import {SummaryItem} from "../summary/SummaryItem";
import TestRunsView from "./TestRunsView";
import TestStatusHistoryView from "./TestStatusHistoryView";
import {BoardItemDefinition} from "@amzn/awsui-board-components/internal/interfaces";

interface BoardItemData {
  title: string;
  content: React.ReactNode | null;
}

interface BoardItemType {
  id: string;
  rowSpan: number;
  columnSpan: number;
  data: BoardItemData;
}

interface UATProjectViewProps {
  projectId: string;
}


export default function UATProjectView({projectId}: UATProjectViewProps) {
  const initialItems: BoardItemDefinition<BoardItemData>[] = useMemo(() => [
    {
      id: "1",
      rowSpan: 6,
      columnSpan: 4,
      minRowSpan: 3,
      maxRowSpan: 12,
      minColumnSpan: 2,
      maxColumnSpan: 6,
      data: {
        title: "Test Runs",
        content: null
      }
    },
    {
      id: "2",
      rowSpan: 4,
      columnSpan: 2,
      minRowSpan: 3,
      maxRowSpan: 12,
      minColumnSpan: 2,
      maxColumnSpan: 6,
      data: {
        title: "Test Status History",
        content: null
      }
    }
  ], []);
  const [items, setItems] = useState<BoardItemDefinition<BoardItemData>[]>(initialItems);
  const [project, setProject] = useState<UATProject | undefined>(undefined);
  const [searchParams] = useSearchParams();
  const [darkMode, setDarkMode] = useState(false);
  const stageParam = searchParams.get("stage") || "Prod";

  // Memoize i18nStrings
  // @ts-ignore
  const i18nStrings: BoardProps.I18nStrings<BoardItemData> = useMemo(() => ({
    liveAnnouncementDndStarted: (position: number) => `Picked up item at position ${position}`,
    liveAnnouncementDndItemReordered: (state: BoardProps.DndReorderState<BoardItemData>) => {
      // @ts-ignore
      const {direction, index} = state;
      return `Item moved ${direction} to position ${index}`;
    },
    liveAnnouncementDndCommitted: (items: BoardItemDefinition<BoardItemData>[]) =>
        `Reordered ${items.length} items`,
    liveAnnouncementDndItemResized: (state: { direction: string; size: number }) =>
        `Item resized ${state.direction} to span ${state.size} ${state.direction === 'horizontal' ? 'columns' : 'rows'}`,
    liveAnnouncementDndItemInserted: (position: number) =>
        `Inserted item at position ${position}`,
    liveAnnouncementDndDiscarded: () => "Cancelled reordering",
    liveAnnouncementItemRemoved: (position: number) =>
        `Item ${position} removed`,
    navigationAriaLabel: "Items",
    navigationItemAriaLabel: (position: number) =>
        `Item ${position}`,
    dragHandleAriaLabel: "Drag handle",
    dragHandleAriaDescription:
        "Use Space or Enter to activate drag, arrow keys to move, Space or Enter to submit, or Escape to cancel.",
    resizeHandleAriaLabel: "Resize handle",
    resizeHandleAriaDescription:
        "Use Space or Enter to activate resize, arrow keys to move, Space or Enter to submit, or Escape to cancel.",
    gridAriaLabel: "Items grid"
  }), []);

  // Initialize items
  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

// Fetch project data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const projectData = await fetchUATProject(projectId);
        setProject(projectData);
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };

    fetchData();
  }, [projectId]);

  // Update items when project changes
  useEffect(() => {
    if (project) {
      const updatedItems: BoardItemDefinition<BoardItemData>[] = [
        {
          ...initialItems[0],
          data: {
            title: "Test Runs",
            content: <TestRunsView project={project}/>
          }
        },
        {
          ...initialItems[1],
          data: {
            title: "Test Status History",
            content: <TestStatusHistoryView project={project}/>
          }
        }
      ];
      setItems(updatedItems);
    }
  }, [project, initialItems]);

  const onItemsChange = (newItems: BoardItemDefinition<BoardItemData>[]) => {
    setItems(newItems);
  };

  return (
      <DarkModeContext.Provider value={darkMode}>
        <StageContext.Provider value={stageParam}>
          {!project || !Array.isArray(items) ? (
              <Box textAlign="center">
                <Spinner size="big"/>
              </Box>
          ) : (
              <AppLayout
                  contentType="default"
                  headerSelector="#h"
                  maxContentWidth={1700}
                  navigationHide
                  toolsHide
                  content={
                    <SpaceBetween size="xl">
                      <ContentLayout defaultPadding>
                        <SpaceBetween size="xl">
                          <BreadcrumbGroup
                              items={[
                                {text: "UAT Projects", href: "/uat"},
                                {text: project.name, href: `/project/${projectId}`}
                              ]}
                              ariaLabel="Breadcrumbs"
                          />
                          <Container
                              header={
                                <SpaceBetween size="m">
                                  <Header variant="h1">
                                    {project.name}
                                  </Header>
                                  <Header variant="h3">
                                    {project.description}
                                  </Header>
                                  <ColumnLayout columns={4} borders="vertical">
                                    <SummaryItem
                                        label="GL"
                                        value={project.GLPG}
                                    />
                                    <SummaryItem
                                        label="Country"
                                        value={project.marketplace}
                                    />
                                    <SummaryItem
                                        label="Status"
                                        value={project.status}
                                    />
                                    <SummaryItem
                                        label="Date"
                                        value={project.created_at}
                                    />
                                  </ColumnLayout>
                                </SpaceBetween>
                              }
                          >
                            <SpaceBetween size="xl">
                              <Board
                                  items={items}
                                  //@ts-ignore
                                  onItemsChange={onItemsChange}
                                  //@ts-ignore
                                  i18nStrings={i18nStrings}
                                  resizeHandles={["right", "bottom", "corner"]}
                                  gridDefinition={{
                                    columns: 12,
                                    rows: 12,
                                    columnGap: 10,
                                    rowGap: 10
                                  }}
                                  renderItem={(item) => (
                                      <BoardItem
                                          //@ts-ignore
                                          header={<Header>{item.data.title}</Header>}
                                          disableContentPaddings={false}
                                          i18nStrings={{
                                            dragHandleAriaLabel: "Drag handle",
                                            dragHandleAriaDescription:
                                                "Use Space or Enter to activate drag, arrow keys to move, Space or Enter to submit, or Escape to cancel.",
                                            resizeHandleAriaLabel: "Resize handle",
                                            resizeHandleAriaDescription:
                                                "Use Space or Enter to activate resize, arrow keys to move, Space or Enter to submit, or Escape to cancel."
                                          }}
                                      >
                                        {
                                          //@ts-ignore
                                          item.data.content
                                        }
                                      </BoardItem>
                                  )}
                              />
                            </SpaceBetween>
                          </Container>
                        </SpaceBetween>
                      </ContentLayout>
                    </SpaceBetween>
                  }
              />
          )
          }
        </StageContext.Provider>
      </DarkModeContext.Provider>
  )
};
